import React, { useState, useCallback, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import {
  useDineItems,
  useDineCourses,
  useDineMenuGroups,
  useDeleteDineItem,
} from 'hooks/api/useDineOps';
import type { DineItem } from 'api/types';
import DineOpsItemModal from 'components/modals/modules/dineops-module/DineOpsItemModal';
import DineOpsCourseGroupModal from 'components/modals/modules/dineops-module/DineOpsCourseGroupModal';
import { canSeeModule } from 'components/modules/print-module/printUi';
import { DINE_MODULES } from '../dineOpsScreens';
import type { DishSection } from './dishWorkspace';

/**
 * MENU-001 — the menu master, kept once for every outlet (DO-312). What each
 * outlet sells, and at what price, is Outlet Items; the Sold in column says
 * which outlets list a dish.
 *
 * Follows .claude/guidelines/ui-design/screen-blueprint.md: ModuleHeader with
 * search and a filter toggle, SyutGrid with pagination and a frozen actions
 * column, and a collapsible filter panel as a sibling of the grid wrapper.
 *
 * WHAT IS NOT EDITABLE HERE, AND WHY
 * Veg, item type, tax group and sold-out are shown but never typed. They are the
 * four columns written by tag answers, and the Attributes tab is the only way to
 * change them. Offering them as fields on this grid would let the column and the
 * tag disagree, and the tag is the one people trust.
 */

interface Filters {
  courseId?: string;
  groupId?: string;
  isOpenPrice?: string;
  isSoldOut?: string;
  includeInactive?: string;
}

const DineOpsMenuItemsScreen: React.FC<{ onNavigateModule?: (moduleId: string) => void }> = ({ onNavigateModule }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [confirmDelete, setConfirmDelete] = useState<DineItem | null>(null);
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; record: DineItem | null; section?: DishSection } | null>(null);
  const [structureOpen, setStructureOpen] = useState(false);
  // CRS Tax Policy for this outlet's property: off means bills carry no tax.
  const [taxOn, setTaxOn] = useState(true);
  useEffect(() => {
    dineOpsService.getOutlet().then((o) => setTaxOn(o?.taxCalculationEnabled !== false)).catch(() => setTaxOn(true));
  }, []);
  /** A grid cell opens the dish window at the section that cell is about. */
  const open = useCallback((record: DineItem, section: DishSection) => setEditing({ mode: 'edit', record, section }), []);
  const cellLink = useCallback((record: DineItem, section: DishSection, children: React.ReactNode, title?: string) => (
    <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} title={title ?? 'Open this part of the dish'}
      onClick={(e) => { e.stopPropagation(); open(record, section); }}>{children}</button>
  ), [open]);

  /*
   * Filtering happens server side, not in a useMemo over a page of rows. The
   * card runs to 133 dishes and the grid pages at 25 — filtering the current
   * page would show "no results" whenever the match sits on page 2, which reads
   * as an empty card rather than a paging artefact.
   */
  const query = useMemo(
    () => ({
      page: 1,
      pageSize: 100,
      searchTerm: searchQuery || undefined,
      courseId: filters.courseId || undefined,
      groupId: filters.groupId || undefined,
      isOpenPrice: filters.isOpenPrice ? filters.isOpenPrice === 'true' : undefined,
      isSoldOut: filters.isSoldOut ? filters.isSoldOut === 'true' : undefined,
      includeInactive: filters.includeInactive === 'true' || undefined,
    }),
    [searchQuery, filters],
  );

  const { data: items, loading, error, refetch } = useDineItems(query);
  const { data: courses, refetch: refetchCourses } = useDineCourses({ pageSize: 100 });
  const { data: groups, refetch: refetchGroups } = useDineMenuGroups({ pageSize: 200 });
  const { remove } = useDeleteDineItem();

  // ------------------------------------------------------------- filter options

  const courseOptions = useMemo(
    () => [
      { value: '', label: 'All categories' },
      ...courses.map((c) => ({ value: String(c.id), label: c.courseName })),
    ],
    [courses],
  );

  const groupOptions = useMemo(() => {
    // Narrow to the chosen course, so the two filters cannot contradict.
    const visible = filters.courseId
      ? groups.filter((g) => String(g.courseId) === filters.courseId)
      : groups;

    return [
      { value: '', label: 'All groups' },
      ...visible.map((g) => ({ value: String(g.id), label: `${g.courseName} · ${g.groupName}` })),
    ];
  }, [groups, filters.courseId]);

  const yesNoOptions = [
    { value: '', label: 'Any' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  // -------------------------------------------------------------------- actions

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleAdd = useCallback(() => {
    setEditing({ mode: 'create', record: null });
  }, []);

  const handleExport = useCallback(() => {
    const header = ['Code', 'Name', 'Category', 'Group', 'Price', 'Open priced', 'Veg', 'Type', 'Tax', 'Sold out', 'Sold in'];
    const rows = items.map((i) => [
      i.itemCode,
      i.itemName,
      i.courseName,
      i.groupName,
      i.isOpenPrice ? '' : String(i.price),
      i.isOpenPrice ? 'Yes' : 'No',
      i.isVeg ? 'Veg' : 'Non-veg',
      i.itemType ?? '',
      i.taxBasis === 'OUTSIDE_GST' ? 'Outside GST' : 'Service',
      i.isSoldOut ? 'Yes' : 'No',
      i.soldIn ?? '',
    ]);

    // Quote every field: dish names contain commas ("Dal Tadka, ghee").
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `menu-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [items]);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) return;

    try {
      await remove(confirmDelete.id, confirmDelete.rowVersion);
      notificationService.success('Removed', `'${confirmDelete.itemName}' is off the card.`);
      setConfirmDelete(null);
      void refetch();
    } catch (err: any) {
      /*
       * Show what the API said. It distinguishes "someone else saved first"
       * (409) from "no such dish" (404), and a generic "delete failed" would
       * throw that away and send the user to look for a problem that is not there.
       */
      const message = err?.response?.data?.message ?? 'The dish could not be removed.';
      notificationService.error('Could not remove the dish', message);
      setConfirmDelete(null);
    }
  }, [confirmDelete, remove, refetch]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: handleAdd, tooltip: 'Add a dish', variant: 'icon' },
      // Courses and groups are the card's structure. Created a handful of times
      // in an outlet's life, so a header button rather than a sidebar entry; labelled, as an icon was missed.
      { id: 'structure', icon: <IconSystem.folder size={18} />, label: 'Categories & groups', onClick: () => setStructureOpen(true), tooltip: 'Categories & menu groups', variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: handleRefresh, tooltip: 'Refresh', variant: 'icon' },
      { id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' },
    ],
    [handleAdd, handleRefresh, handleExport],
  );

  // -------------------------------------------------------------------- columns

  const columns: SyutGridColumn<DineItem>[] = useMemo(
    () => [
      { id: 'itemCode', header: 'Code', accessorKey: 'itemCode', enableSorting: true, size: 90 },
      { id: 'itemName', header: 'Dish', accessorKey: 'itemName', enableSorting: true, size: 220 },
      { id: 'courseName', header: 'Category', accessorKey: 'courseName', enableSorting: true, size: 130 },
      { id: 'groupName', header: 'Group', accessorKey: 'groupName', enableSorting: true, size: 160 },
      {
        id: 'price',
        header: 'Price',
        accessorKey: 'price',
        enableSorting: true,
        size: 110,
        // An open-priced dish has no list price; 0.00 would read as free.
        cell: ({ row }) =>
          row.original.isOpenPrice
            ? <span style={{ opacity: 0.7, fontStyle: 'italic' }}>Open</span>
            : row.original.price.toFixed(2),
      },
      {
        id: 'isVeg',
        header: 'Food type',
        accessorKey: 'isVeg',
        enableSorting: true,
        size: 110,
        cell: ({ row }) => (row.original.isVeg ? 'Veg' : 'Non-veg'),
      },
      { id: 'itemType', header: 'Type', accessorKey: 'itemType', enableSorting: true, size: 110 },
      {
        id: 'taxGroupCode',
        header: 'Tax',
        accessorKey: 'taxGroupCode',
        enableSorting: true,
        size: 120,
        // Every dish bills as restaurant service under the outlet's tax profile, except Outside GST (liquor).
        // A dish recorded as goods also bills as service for now (decided 2026-10-28).
        cell: ({ row }) => cellLink(row.original, 'tax', !taxOn
          ? <span style={{ color: 'var(--text-muted)' }}>Tax off</span>
          : row.original.taxBasis === 'OUTSIDE_GST' ? 'Outside GST' : 'Service',
        !taxOn ? 'Tax calculation is off for this property' : 'Taxed under each outlet\'s tax profile'),
      },
      {
        id: 'effectiveMinutesToTable',
        header: 'To table',
        accessorKey: 'effectiveMinutesToTable',
        enableSorting: true,
        size: 100,
        // Falls back to the menu group's time, so a dash means neither is set.
        cell: ({ row }) =>
          row.original.effectiveMinutesToTable ? `${row.original.effectiveMinutesToTable} min` : '—',
      },
      {
        id: 'variantCount',
        header: 'Sizes',
        accessorKey: 'variantCount',
        enableSorting: true,
        size: 80,
        cell: ({ row }) => cellLink(row.original, 'variants', row.original.variantCount > 0 ? String(row.original.variantCount) : '—'),
      },
      {
        id: 'isSoldOut',
        header: 'Availability',
        accessorKey: 'isSoldOut',
        enableSorting: true,
        size: 120,
        cell: ({ row }) =>
          row.original.isSoldOut
            ? <span style={{ color: 'var(--color-danger, #c0392b)', fontWeight: 600 }}>Sold out</span>
            : 'Available',
      },
      {
        id: 'soldIn',
        header: 'Sold in',
        accessorKey: 'soldIn',
        enableSorting: true,
        size: 130,
        // One menu for every outlet: a dish no outlet lists is on no card.
        cell: ({ row }) => cellLink(row.original, 'soldin', row.original.soldIn ?? <span style={{ color: 'var(--text-muted)' }}>no outlet</span>),
      },
    ],
    [taxOn, cellLink],
  );

  const getRowActions = useCallback(
    (row: DineItem) => [
      {
        id: 'edit',
        label: 'Edit',
        icon: <IconSystem.edit size={16} />,
        onClick: () => setEditing({ mode: 'edit', record: row }),
      },
      {
        id: 'delete',
        label: 'Remove from the menu',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setConfirmDelete(row),
        variant: 'danger' as const,
      },
    ],
    [],
  );

  // --------------------------------------------------------------------- render

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Menu Items"
        description="The menu, kept once for every outlet: each dish, its category and group, menu price and attributes"
        icon={<IconSystem.list size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search by dish name or code',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          tooltip: 'Toggle filters',
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={items}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={
                error
                  ? 'The card could not be loaded. If DineOps was just installed, it may not be enabled yet.'
                  : 'No dishes match. Clear the search or the filters to see the whole card.'
              }
            />
          </div>

          {/* Filter panel — sibling of the grid wrapper, per the blueprint */}
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Category"
                  options={courseOptions}
                  value={filters.courseId || ''}
                  onChange={(value) =>
                    // Clearing the course clears the group too: a group filter
                    // from another course would return nothing and look broken.
                    setFilters((prev) => ({
                      ...prev,
                      courseId: (value as string) || undefined,
                      groupId: undefined,
                    }))
                  }
                  placeholder="All categories"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Menu group"
                  options={groupOptions}
                  value={filters.groupId || ''}
                  onChange={(value) => setFilters((prev) => ({ ...prev, groupId: (value as string) || undefined }))}
                  placeholder="All groups"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Open priced"
                  options={yesNoOptions}
                  value={filters.isOpenPrice || ''}
                  onChange={(value) => setFilters((prev) => ({ ...prev, isOpenPrice: (value as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Sold out"
                  options={yesNoOptions}
                  value={filters.isSoldOut || ''}
                  onChange={(value) => setFilters((prev) => ({ ...prev, isSoldOut: (value as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Include inactive"
                  options={yesNoOptions.filter((o) => o.value !== 'false')}
                  value={filters.includeInactive || ''}
                  onChange={(value) => setFilters((prev) => ({ ...prev, includeInactive: (value as string) || undefined }))}
                  placeholder="Active only"
                  size="sm"
                  isClearable
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {structureOpen && (
        <DineOpsCourseGroupModal
          isOpen
          onClose={() => setStructureOpen(false)}
          onChanged={() => { void refetchCourses(); void refetchGroups(); void refetch(); }}
        />
      )}

      {editing && (
        <DineOpsItemModal
          isOpen
          mode={editing.mode}
          record={editing.record}
          groups={groups}
          initialSection={editing.section}
          onSave={() => void refetch()}
          onClose={() => setEditing(null)}
          onOpenOutletItems={onNavigateModule && canSeeModule(DINE_MODULES.OUTLET_ITEMS)
            ? () => { setEditing(null); onNavigateModule(DINE_MODULES.OUTLET_ITEMS); }
            : undefined}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          open
          title="Remove this dish from the menu?"
          message={
            `'${confirmDelete.itemName}' (${confirmDelete.itemCode}) leaves the menu and every outlet that sells it`
            + (confirmDelete.soldIn ? ` (${confirmDelete.soldIn}). ` : '. ')
            + 'To stop selling it in one outlet only, use Outlet Items. '
            + 'Past orders and bills keep their own copy of the name and price, so history is unaffected.'
          }
          confirmLabel="Remove"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default DineOpsMenuItemsScreen;
